import { useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useLocation } from 'react-router';
import { useCoupons } from 'hooks/useFetchCoupons';
import { AppointmentSteps, type Flow, FLOW_MODES, type Steps } from 'types/appointments';
import { getUserId } from 'utils/storage';
import { ConfirmationStep } from '../components/Steps/ConfirmationStep';
import { DateTimeStep } from '../components/Steps/DateTimeStep';
import { PaymentStep } from '../components/Steps/PaymentStep';
import { PsychologistStep } from '../components/Steps/PsychologistStep';
import { SearchDateTimeStep } from '../components/Steps/SearchDateTimeStep';
import { SearchPsychologistStep } from '../components/Steps/SearchPsychologistStep';
import { SearchSpecialityStep } from '../components/Steps/SearchSpecialityStep';
import { SpecialityStep } from '../components/Steps/SpecialityStep';
import {
  AppointmentsReservePayload,
  type AppointmentsReserveFormData,
  type StepProps,
} from '../types';

export const INITIAL_STEP_INDEX = 0;

type StepsByFlow = {
  [FLOW_MODES.regular]: typeof STEPS;
  [FLOW_MODES.search]: typeof SEARCH_STEPS;
  [FLOW_MODES.edit]: typeof OUTSIDE_STEPS;
  [FLOW_MODES.recent_psychologist]: typeof OUTSIDE_STEPS;
};

export type StepDefinition = {
  id: number;
  name: Steps;

  component: React.ComponentType<StepProps>;
};

const STEPS: ReadonlyArray<{ name: Steps; component: React.ComponentType<StepProps> }> = [
  { name: AppointmentSteps.speciality, component: SpecialityStep },
  { name: AppointmentSteps.schedule, component: DateTimeStep },
  { name: AppointmentSteps.psychologist, component: PsychologistStep },
  { name: AppointmentSteps.payment, component: PaymentStep },
  { name: AppointmentSteps.resume, component: ConfirmationStep },
];

const SEARCH_STEPS: ReadonlyArray<{ name: Steps; component: React.ComponentType<StepProps> }> = [
  { name: AppointmentSteps.psychologist, component: SearchPsychologistStep },
  { name: AppointmentSteps.speciality, component: SearchSpecialityStep },
  { name: AppointmentSteps.schedule, component: SearchDateTimeStep },
  { name: AppointmentSteps.payment, component: PaymentStep },
  { name: AppointmentSteps.resume, component: ConfirmationStep },
];

const OUTSIDE_STEPS: ReadonlyArray<{ name: Steps; component: React.ComponentType<StepProps> }> = [
  { name: AppointmentSteps.schedule, component: SearchDateTimeStep },
  { name: AppointmentSteps.payment, component: PaymentStep },
  { name: AppointmentSteps.resume, component: ConfirmationStep },
];

const STEPS_BY_FLOW: StepsByFlow = {
  [FLOW_MODES.regular]: STEPS,
  [FLOW_MODES.search]: SEARCH_STEPS,
  [FLOW_MODES.edit]: OUTSIDE_STEPS,
  [FLOW_MODES.recent_psychologist]: OUTSIDE_STEPS,
};

interface UseStepNavigationResult {
  steps: StepDefinition[];
  currentStepIndex: number;
  currentStep: StepDefinition;
  handleNext: () => void;
  handleBack: (index?: number) => void;
}

const getInitialStepName = (rawStep: number | Steps | undefined): Steps => {
  if (typeof rawStep === 'string' && rawStep in AppointmentSteps) {
    return rawStep as Steps;
  }
  return STEPS[Number(rawStep)]?.name ?? AppointmentSteps.speciality;
};

export const useStepNavigation = (): UseStepNavigationResult => {
  const { state } = useLocation();
  const patientId = state?.patientId ?? getUserId();
  const { watch } = useFormContext<AppointmentsReserveFormData>();
  const flow = watch(AppointmentsReservePayload.flow);
  const specialityBooked = watch(AppointmentsReservePayload.specialityBooked);
  const stepsByFlow = STEPS_BY_FLOW[flow as keyof StepsByFlow];

  const { remainingSessions, remainingSessionsThisMonth, hasCopaid } = useCoupons(
    patientId,
    specialityBooked
  );
  const hasSessions = remainingSessions > 0 || (!hasCopaid && remainingSessionsThisMonth > 0);
  const isReturningFromCheckoutRef = useRef(Boolean(state?.checkoutData));
  const shouldNotShowPaymentStep =
    !isReturningFromCheckoutRef.current && (hasSessions || flow === FLOW_MODES.edit);

  const steps = useMemo<StepDefinition[]>(() => {
    const filtered = shouldNotShowPaymentStep
      ? stepsByFlow.filter(step => step.name !== AppointmentSteps.payment)
      : stepsByFlow;
    return filtered.map((step, index) => ({ ...step, id: index + 1 }));
  }, [shouldNotShowPaymentStep, stepsByFlow]);

  const [currentStepName, setCurrentStepName] = useState<Steps>(() =>
    getInitialStepName(state?.step)
  );
  const [prevFlow, setPrevFlow] = useState<Flow>(flow);

  if (flow !== prevFlow) {
    setPrevFlow(flow);
    setCurrentStepName(stepsByFlow[0].name);
  }

  const currentStepIndex = useMemo(() => {
    const index = steps.findIndex(step => step.name === currentStepName);
    return index === -1 ? 0 : index;
  }, [steps, currentStepName]);

  const currentStep = steps[currentStepIndex] ?? steps[0];

  const handleNext = (): void => {
    const nextIndex = Math.min(currentStepIndex + 1, steps.length - 1);
    setCurrentStepName(steps[nextIndex].name);
  };

  const handleBack = (index?: number): void => {
    const prevIndex = index ?? Math.max(currentStepIndex - 1, 0);
    setCurrentStepName(steps[prevIndex].name);
    if (index === 0) {
      isReturningFromCheckoutRef.current = false;
    }
  };

  return {
    steps,
    currentStepIndex,
    currentStep,
    handleNext,
    handleBack,
  };
};
